import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { serverGet, serverPut, serverDelete } from "../../middleware/http";
import { apiConst, appConst } from "../constant/constant";
import NotifyContext from "./notify";
import ProgressContext from "./progress";
import {
    ecnSession,
    getMemberId,
    getSearchParams,
    isArray,
    updateSearchParams,
} from "./validation";
import { useManageResource } from "../Context/ManageResourceContext";

const ProfileContext = createContext();

export const useProfile = () => {
    return useContext(ProfileContext);
};

export const ProfileProvider = ({ children }) => {
    const [profileContext, setProfileContext] = useState(null);
    const [patientInfo, setPatientInfo] = useState();
    const [imageUrl, setImageUrl] = useState();
    const [contextFamilyList, setContextFamilyList] = useState(null);
    const [isfamilyMember, setisFamilyMember] = useState(false);
    const [isAuth, setIsAuth] = useState(false);
    const [checkedProfileData, setCheckedProfileData] = useState(null);

    const { notifyToast } = useContext(NotifyContext);
    const { progParams } = useContext(ProgressContext);
    const { resourceList, getCurrentResource } = useManageResource();

    const location = useLocation();

    // These fetches take seconds, while the effects below re-run on every step change and read
    // patientInfo/profileContext/contextFamilyList from a render closure that is still empty —
    // so without an in-flight marker each step starts another copy of a request already running.
    const fetchingRef = useRef({ patient: false, profile: false });
    const familyListPromiseRef = useRef(null);
    const profilePromiseRef = useRef(null);
    // Guards updateResidingState() below: getPatient() re-evaluates the residing-state condition
    // on every successful fetch, so overlapping getPatient() calls (e.g. the boot effect racing a
    // caller elsewhere) could each independently fire their own duplicate PUT before the first
    // one's rsidstatus=y URL update is even relevant.
    const residingStateUpdateRef = useRef(false);

    // Functional updater, not a closure read of profileContext: this is called by nearly every
    // save flow (ContactInfo.js, PersonalInfo.js, ProfilePreference.js, EmergencyContactInfo.js,
    // AddProfile.js, ShippingAddress.js, insForm.js...), and a plain `{...profileContext, ...}`
    // read could race getPatient()/getprofile()'s own merges (both fire at boot) and silently
    // clobber whichever of them landed first.
    const updateProfile = (newProfile) => {
        const _id = newProfile?.resProfile?.id ?? profileContext?.resProfile?.id;
        setProfileContext((prev) => ({ ...(prev || {}), ...newProfile }));
        ecnSession(_id, "mid");
        localStorage.setItem("memberId", _id);
    };

    // PUP (profile/contact/emergency-contact) and IIN (documents) are two different steps that
    // both read off the one checkedProfileData snapshot checkUserProfile() populates -- consuming
    // clears only the caller's own fields, never the whole object, so whichever step is reached
    // first doesn't destroy the other's still-unread data. A later revisit to the same step in the
    // same session (e.g. Back then Forward, after a save) then correctly falls through to a fresh
    // fetch instead of ever re-showing the pre-save snapshot.
    const clearCheckedProfileFields = (fields) => {
        setCheckedProfileData((prev) => {
            if (!prev) return prev;
            const next = { ...prev };
            fields.forEach((f) => { next[f] = null; });
            return next;
        });
    };

    // Shares the in-flight fetch so a concurrent caller (e.g. Service.js's getPrimaryPhoneNumber)
    // awaits the same request instead of firing a duplicate one. Deliberately NOT cached beyond
    // that -- submitProfile/deleteProfilePhoto call this to force a fresh refetch after a
    // mutation, so an "already loaded, skip" fast path would wrongly hand back stale data.
    const getprofile = async () => {
        if (fetchingRef.current.profile) {
            return await profilePromiseRef.current;
        }

        fetchingRef.current.profile = true;
        profilePromiseRef.current = (async () => {
            try {
                const res = await serverGet(apiConst.getuserprofile);
                if (res.data != null) {
                    const { contact: resContact, emergencyContact: resEmergencyContact, ...resProfile } = res.data;
                    const response = {
                        resProfile,
                        resContact,
                        // AddProfile.js's standalone route reuses this instead of re-fetching the
                        // same apiConst.getuserprofile endpoint itself.
                        resEmergencyContact,
                    };
                    ecnSession(resProfile?.id, "mid");
                    localStorage.setItem("memberId", resProfile?.id);
                    // Merge, not replace -- getPatient() below can update resPatient on
                    // profileContext independently, and a plain replace here would wipe it out
                    // if this response lands after that one (both fire together at boot).
                    setProfileContext((prev) => ({ ...(prev || {}), ...response }));
                    return response;
                }
            } catch (error) {
                console.log(error);
            } finally {
                fetchingRef.current.profile = false;
            }
        })();
        return await profilePromiseRef.current;
    };

    const submitProfile = (data) => {
        progParams(true);
        serverPut(
            `${apiConst.updateprofilephoto}/${profileContext?.resProfile?.id}`,
            { photo: data }
        )
            .then((res) => {
                progParams(false);
                try {
                    if (res?.status == 1) {
                        setImageUrl(data);
                        getprofile();
                        notifyToast(res.messageCode[0]);
                    } else {
                        var error = JSON.parse(res?.data?.errorMessage);
                        notifyToast(error?.messageCode[0]);
                    }
                } catch (e) {
                    console.log(e);
                }
            })
            .catch((e) => {
                console.log(e);
                var error = JSON.parse(e?.response?.data?.errorMessage);
                notifyToast(error?.messageCode[0]);
            });
    };

    const deleteProfilePhoto = () => {
        progParams(true);
        serverDelete(
            `${apiConst.deleteprofilephoto}/${profileContext?.resProfile?.id}`,
            ""
        )
            .then((res) => {
                progParams(false);
                try {
                    if (res.status === 1) {
                        setImageUrl(null);
                        notifyToast(res.messageCode[0]);
                        getprofile();
                    } else {
                        var error = JSON.parse(res?.data?.errorMessage);
                        notifyToast(error?.messageCode[0]);
                    }
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const getPatient = () => {
        if (fetchingRef.current.patient) {
            return;
        }

        fetchingRef.current.patient = true;
        serverGet(apiConst.patientinfo).then((res) => {
            // Released before the body runs, not in a finally: the residing-state branch below
            // calls getPatient() again on purpose, and that legitimate refetch must not be
            // mistaken for the duplicate this marker exists to stop.
            fetchingRef.current.patient = false;
            try {
                if (res?.data != null) {
                    setPatientInfo(res?.data);
                    // Merge onto profileContext too (not a plain replace -- see getprofile()'s
                    // own merge above for why), so the shared context always carries the
                    // latest patient details alongside profile/contact.
                    setProfileContext((prev) => ({ ...(prev || {}), resPatient: res?.data }));
                    localStorage.setItem("memberId", res?.data?.memberId);

                    const residingStateId = getSearchParams("rsid");
                    const residingStateIdStatus = getSearchParams("rsidstatus"); // status updtaed to y else n
                    if (
                        residingStateIdStatus != "y" &&
                        residingStateId &&
                        residingStateId?.trim() &&
                        !residingStateUpdateRef.current
                    ) {
                        updateResidingState({
                            residingStateId: residingStateId,
                            memberId: res?.data?.memberId,
                        });
                    }
                    // Deliberately no "bootstrap rsid from the patient record when the URL has
                    // none yet" branch here (removed) -- this provider has no way to know whether
                    // the currently selected service's PLR step (and the account's SCL setting)
                    // even call for a residing state, so doing that here unconditionally could
                    // resurrect a patient-level value into the URL for a service that doesn't ask
                    // for one. Service.js's handleServiceFlow already does this exact fallback
                    // itself, correctly gated on the service's own workflow steps.

                } else {
                    //var error = JSON.parse(res?.data?.errorMessage);
                    //notifyToast(error?.messageCode[0]);
                }
            } catch (error) {
                console.log(error);
            }
        }).catch((e) => {
            fetchingRef.current.patient = false;
            console.log(e);
        });
    };
    function updateResidingState(data) {
        // Blocks a second overlapping getPatient() call from re-triggering this same PUT before
        // this one's rsidstatus=y URL update lands -- without this, two getPatient() calls that
        // both observe rsidstatus != "y" each independently fire their own duplicate save.
        residingStateUpdateRef.current = true;
        //progParams(true);
        serverPut(apiConst.updateResiding, data).then((res) => {
            //progParams(false);
            try {
                if (res?.status == 1) {
                    updateSearchParams("rsidstatus", "y");
                    getPatient();
                } else {
                    //var error = JSON.parse(res?.data?.errorMessage);
                    //notifyToast(error?.messageCode[0]);
                }
            } catch (error) {
                console.log(error);
            } finally {
                residingStateUpdateRef.current = false;
            }
        }).catch((e) => {
            residingStateUpdateRef.current = false;
            console.log(e);
        });
    }

    // user/checkuserprofile is the most expensive single call in the intake (measured at 5.9–7.1 s),
    // and every arrival at the Reason step pays for it twice — once when Service.js decides the step,
    // again when Reason.js re-verifies just before submit. Nothing between those two points can change
    // the answer, so the verdict is kept and reused.
    //
    // What CAN change it is the patient completing the profile (PUP) or the ID upload (IIN), so
    // the key is exactly that: whether those steps have been passed through. Back-navigation pops
    // entries out of serviceFlowHistory, so leaving one of them re-asks the question too.
    //
    // Deliberately NOT keyed on the whole of serviceFlowHistory, which grows as each step merely
    // renders (REC appends itself between Service.js's check and Reason.js's) and would miss every
    // reuse this exists for. Deliberately not keyed on fmid either: every call behind this endpoint
    // is about the signed-in user — their own profile, their own contacts, patient/getinfo for
    // self, their own ID documents — so which family member the booking is for cannot change the
    // answer. `force` bypasses the reuse outright.
    const profileVerdictRef = useRef({ key: null, value: null });

    const buildProfileVerdictKey = () => {
        const history = ecnSession()?.serviceFlowHistory;

        return isArray(history)
            ? history.filter((step) => step === "PUP" || step === "IIN").join(",")
            : "";
    };

    // Holds the check currently in flight. The verdict above is only recorded once the answer has
    // landed, so without this a warm-up started in the background and the step that later asks for
    // the same answer both pay full price for it — the warm-up would buy nothing. Joining the
    // request that is already running is what makes a warm-up worth starting.
    const checkProfileRequestRef = useRef({ key: null, promise: null });

    // Each caller gets its own copy of the answer. Service.js's REC gate writes to the verdict it
    // is handed (an unverified external identity session flips idVerification back to false), and
    // now that one answer can be shared — by the memo above, by a warm-up, and by whoever joins it
    // — that write has to stay with the caller who made it instead of reaching the others.
    const asVerdict = (value) => (value ? { ...value } : value);

    // `silent` suppresses the progress overlay. progParams renders a full-screen backdrop that
    // blocks the page, which is right when a step is waiting on this answer and wrong when it is
    // only being warmed up in the background — a warm-up must never freeze the page the patient
    // is currently using. It belongs to the caller, not to the request: a step joining a silent
    // warm-up still blocks, because that step really is waiting.
    const checkUserProfile = async (force = false, silent = false) => {
        const verdictKey = buildProfileVerdictKey();

        if (!force && profileVerdictRef.current.value && profileVerdictRef.current.key === verdictKey) {
            return asVerdict(profileVerdictRef.current.value);
        }

        const _inFlight = checkProfileRequestRef.current;

        if (!force && _inFlight.promise && _inFlight.key === verdictKey) {
            try {
                if (!silent) progParams(true);
                return asVerdict(await _inFlight.promise);
            } finally {
                if (!silent) progParams(false);
            }
        }

        const request = runCheckUserProfile(verdictKey);

        checkProfileRequestRef.current = { key: verdictKey, promise: request };

        try {
            if (!silent) progParams(true);
            return asVerdict(await request);
        } finally {
            // Cleared once settled: the verdict memo already covers "asked and answered", and a
            // negative answer is exactly what the callers are meant to be able to ask again.
            if (checkProfileRequestRef.current.promise === request) {
                checkProfileRequestRef.current = { key: null, promise: null };
            }
            if (!silent) progParams(false);
        }
    };

    const runCheckUserProfile = async (verdictKey) => {
        try {
            const returnData = (value) => {
                if (value) {
                    return JSON.parse(value);
                }
            };

            const res = await serverGet("user/checkuserprofile");

            const _profile = returnData(res?.data?.profile);
            const _contact = returnData(res?.data?.contact);
            const _emergencyContact = returnData(res?.data?.emergencyContact);
            const _doc = returnData(res?.data?.idVerification);
            const _resource = returnData(res?.data?.resource);
            const _patient = returnData(res?.data?.patient);

            // Hands off profile/contact/emergency-contact/ID-document data to AddProfile.js and
            // ProofUpload.js, which otherwise independently re-fetch equivalent data (the ID
            // document fetch here uses the same typeid=4/gn=identification_type params ProofUpload
            // uses). Not cleared after read -- unlike a step-transition hand-off, this is the same
            // patient's own profile for the rest of the booking session, so continuing to serve it
            // from context (same pattern profileContext already uses for `profileContext` itself)
            // is correct, not stale.
            setCheckedProfileData({
                resProfile: _profile?.data,
                resContact: _contact?.data,
                resEmergencyContact: _emergencyContact?.data,
                resDocuments: _doc?.status == 1 ? _doc?.data : null,
            });

            let _resourceList = getCurrentResource(
                appConst.resource.moduleMenu,
                "EQ7",
                _resource?.data?.resources
            );

            const _isResContact = _resourceList?.find((list) => list?.code === "CIN");
            const _isResEmergencyContact = _resourceList?.find(
                (list) => list?.code === "EMC"
            );
            const isContact =
                (_patient && !_patient?.data?.parentId && _isResContact) || false;
            const isEmergencyContact =
                (_patient && !_patient?.data?.parentId && _isResEmergencyContact) ||
                false;
            const isPersonal = areValuesAssigned(_profile?.data, [
                "dob",
                "genderId",
                "phone",
            ]);

            const _profileValidation = {
                pofileCompleted:
                    (isContact ? Boolean(_contact?.data?.address1) && isPersonal : isPersonal) &&
                    (!isEmergencyContact || _emergencyContact?.data != null),
                idVerification: _doc?.status == 1 && isArray(_doc?.data),
            };
            //setProfileValidation(_profileValidation);

            // Only a fully positive verdict is kept. A negative one is exactly what the callers
            // deliberately re-ask (the patient may have just completed PUP or IIN), so it is never
            // served from here — which keeps this a pure saving with no decision left behind it.
            if (_profileValidation.pofileCompleted && _profileValidation.idVerification) {
                profileVerdictRef.current = { key: verdictKey, value: _profileValidation };
            } else {
                profileVerdictRef.current = { key: null, value: null };
            }

            return _profileValidation;
        } catch (e) {
            console.error(e);
        }
    };

    // The verdict is a pure function of the family list, the menu resources and whether the
    // patient is themselves a dependent — nothing in it needs the network once those are in hand.
    const deriveIsFamilyMember = (familyMembers, patient) => {
        const _resourceList = getCurrentResource(appConst.resource.menu);

        return (
            isArray(familyMembers) &&
            familyMembers.some((list) => list?.statusId == "1" && list?.lockoutEnabled !== true) &&
            _resourceList?.filter((list) => list?.code == "SK6")?.length > 0 &&
            !patient?.parentId
        ) || false;
    };

    // Callers await the returned verdict (e.g. Service.js's REC branch), so an overlapping call
    // shares the in-flight promise and gets the same answer rather than being skipped and
    // silently receiving undefined.
    // `useCached` is opt-in, and only for callers that want the verdict rather than a refresh.
    // Every existing caller leaves it off and still refetches: FamilyMemberAdd calls this
    // precisely to pull in the member it just saved, and answering that from the list held
    // before the save would leave the new child missing from the flow.
    const getFamilyList = async (useCached = false) => {
        if (familyListPromiseRef.current) {
            return await familyListPromiseRef.current;
        }

        // Already loaded, and the caller only wants the verdict: it is a pure function of what
        // context is already holding, so there is nothing to fetch.
        if (useCached && isArray(contextFamilyList) && patientInfo) {
            const _cachedVerdict = deriveIsFamilyMember(contextFamilyList, patientInfo);
            if (_cachedVerdict) setisFamilyMember(true);
            return _cachedVerdict;
        }

        familyListPromiseRef.current = (async () => {
            try {
                // progParams(true);
                const _memberId = await getMemberId();
                let _familyMember = contextFamilyList;
                const _res = await serverGet(`${apiConst.familymemberget}${_memberId}`);
                _familyMember = _res?.data;
                let _patientInfo = patientInfo;

                if (!_patientInfo) {
                    const _res = await serverGet(apiConst.patientinfo);
                    _patientInfo = _res?.data;
                }

                // progParams(false);

                if (_familyMember && _familyMember?.length > 0) {
                    const _isFamilyMember = deriveIsFamilyMember(_familyMember, _patientInfo);

                    if (_isFamilyMember) {
                        setisFamilyMember(true);
                    }
                    setContextFamilyList(_familyMember);
                    return _isFamilyMember;
                } else {
                    setContextFamilyList([]);
                }
                return false;
            } catch (e) {
                console.log(e);
            }
        })();

        try {
            return await familyListPromiseRef.current;
        } finally {
            familyListPromiseRef.current = null;
        }
    };

    function areValuesAssigned(obj, keys) {
        if (obj)
            return keys.every(
                (key) => obj.hasOwnProperty(key) && obj[key] !== null && obj[key] !== ""
            );
    }

    const handleAuth = (data) => {
        setIsAuth(data);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (isAuth) {
                    if (!patientInfo) {
                        getPatient();
                    }
                    if (!profileContext) {
                        await getprofile();
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, [location, isAuth]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (
                    patientInfo &&
                    isAuth &&
                    !Array.isArray(contextFamilyList) &&
                    isArray(resourceList?.resources)
                ) {
                    await getFamilyList();
                }
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, [location, resourceList?.resources, isAuth, patientInfo]);

    return (
        <ProfileContext.Provider
            value={{
                profileContext,
                handleAuth,
                updateProfile,
                submitProfile,
                imageUrl,
                deleteProfilePhoto,
                patientInfo,
                getPatient,
                checkUserProfile,
                checkedProfileData,
                clearCheckedProfileFields,
                contextFamilyList,
                isfamilyMember,
                getFamilyList,
                setContextFamilyList,
                getprofile,
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
};
